import { defineStore } from 'pinia'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import type { AppUser, UserRole } from '@/types'
import { isUserRole } from '@/types'

export type { UserRole, AppUser }

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as AppUser | null,
    isLoading: true,
    error: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    isEditor: (state) => state.user?.role === 'editor' || state.user?.role === 'admin',
    isWriter: (state) => !!state.user,
  },

  actions: {
    async fetchUserRole(uid: string): Promise<UserRole> {
      const userDoc = await getDoc(doc(db, 'users', uid))
      const data = userDoc.data()
      const role = data?.role
      return isUserRole(role) ? role : 'writer'
    },

    mapFirebaseUser(fb: FirebaseUser | null, role: UserRole = 'writer'): AppUser | null {
      if (!fb) return null
      return {
        id: fb.uid,
        email: fb.email ?? '',
        displayName: fb.displayName ?? null,
        photoURL: fb.photoURL ?? null,
        role,
      }
    },

    async init(): Promise<void> {
      return new Promise((resolve) => {
        const unsub = onAuthStateChanged(auth, async (fbUser) => {
          try {
            if (!fbUser) {
              this.user = null
              this.isLoading = false
              unsub()
              resolve()
              return
            }
            const role = await this.fetchUserRole(fbUser.uid)
            this.user = this.mapFirebaseUser(fbUser, role)
          } catch {
            this.user = this.mapFirebaseUser(fbUser, 'writer')
          } finally {
            this.isLoading = false
            unsub()
            resolve()
          }
        })
      })
    },

    async signIn(email: string, password: string) {
      this.error = null
      try {
        const { user } = await signInWithEmailAndPassword(auth, email, password)
        const role = await this.fetchUserRole(user.uid)
        this.user = this.mapFirebaseUser(user, role)
        return true
      } catch (e: unknown) {
        this.error = normalizeAuthError(e, 'Giriş başarısız')
        return false
      }
    },

    async signUp(email: string, password: string, role: UserRole, displayName?: string) {
      this.error = null
      try {
        const { user } = await createUserWithEmailAndPassword(auth, email, password)
        await setDoc(doc(db, 'users', user.uid), {
          email,
          displayName: displayName ?? null,
          role,
          createdAt: new Date().toISOString(),
        })
        this.user = this.mapFirebaseUser(user, role)
        return true
      } catch (e: unknown) {
        this.error = normalizeAuthError(e, 'Kayıt başarısız')
        return false
      }
    },

    async logout() {
      try {
        await signOut(auth)
      } finally {
        this.user = null
        this.error = null
      }
    },
  },
})

/** Map Firebase/auth errors to user-friendly Turkish messages. */
function normalizeAuthError(e: unknown, fallback: string): string {
  if (e instanceof Error) {
    const code = (e as { code?: string }).code
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') return 'E-posta veya şifre hatalı.'
    if (code === 'auth/user-not-found') return 'Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı.'
    if (code === 'auth/email-already-in-use') return 'Bu e-posta adresi zaten kullanılıyor.'
    if (code === 'auth/weak-password') return 'Şifre en az 6 karakter olmalıdır.'
    if (code === 'auth/invalid-email') return 'Geçerli bir e-posta adresi girin.'
    return e.message || fallback
  }
  return fallback
}
